import type { ApiProduct, ApiSku } from "@/lib/api";

/**
 * The money on the product page, kept free of React so it can be tested.
 *
 * Add-ons are separate cart lines with their own prices; the page still shows
 * one running total so the button says what the basket will say.
 */
export type Addon = NonNullable<ApiProduct["addons"]>[number];
export type LoadedAddon = Addon & { sku: ApiSku };

/** The chosen add-ons that actually arrived with a SKU, in the order chosen. */
export function selectedAddons(product: Pick<ApiProduct, "addons">, ids: string[]): LoadedAddon[] {
  const list = product.addons ?? [];
  const out: LoadedAddon[] = [];
  for (const id of ids) {
    const found = list.find((a) => a.skuId === id);
    if (found?.sku) out.push(found as LoadedAddon);
  }
  return out;
}

export function addonsTotal(product: Pick<ApiProduct, "addons">, ids: string[]): number {
  return selectedAddons(product, ids).reduce((n, a) => n + a.sku.priceIdr, 0);
}

/** Variant plus add-ons. Undefined until a variant is resolved — nothing to total. */
export function orderTotal(
  selected: Pick<ApiSku, "priceIdr"> | undefined,
  product: Pick<ApiProduct, "addons">,
  ids: string[],
): number | undefined {
  if (!selected) return undefined;
  return selected.priceIdr + addonsTotal(product, ids);
}

export interface Savings {
  amount: number;
  percent: number;
}

/**
 * What the compare-at price saves, or null when there is nothing honest to say:
 * no compare-at, a compare-at no higher than the price, or a package whose
 * price the server has flagged invalid (its contents got dearer than it).
 */
export function savings(
  sku: Pick<ApiSku, "priceIdr" | "compareAtIdr" | "priceInvalid">,
): Savings | null {
  const compareAt = sku.compareAtIdr;
  if (!compareAt || sku.priceInvalid || compareAt <= sku.priceIdr) return null;
  const amount = compareAt - sku.priceIdr;
  return { amount, percent: Math.floor((amount / compareAt) * 100) };
}
